import { Link } from "react-router-dom";
import { ArrowRight, Shield, FileText, AlertTriangle, Scale } from "lucide-react";
import { Button } from "../components/ui/button";

export default function DisclaimerPage() {
  return (
    <div className="min-h-screen bg-gray-50/50 py-12">
      <div className="container-main">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-[#5BB5A2]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Scale className="w-8 h-8 text-[#5BB5A2]" />
            </div>
            <h1 className="heading-2 mb-4">Disclaimer & Privacy</h1>
            <p className="text-gray-600">
              Important information about our pre-qualification service
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Pre-Qualification Disclaimer */}
            <div className="card-base" data-testid="disclaimer-section">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-[#E8B84A]" />
                <h2 className="text-xl font-semibold text-[#0F3D3E]">Pre-Qualification Disclaimer</h2>
              </div>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 mb-4">
                  The pre-qualification results provided by Financify are for informational purposes only 
                  and should not be considered as a guarantee of loan approval or specific loan terms.
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-[#5BB5A2] rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      <strong>Not a Loan Approval:</strong> This pre-qualification is not a commitment to 
                      lend or a guarantee of loan approval. Final approval is subject to verification of 
                      information provided and completion of the bank's formal application process.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-[#5BB5A2] rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      <strong>Estimated Figures:</strong> All loan amounts, monthly payments, and interest 
                      rates shown are estimates and may vary based on final verification by the lending institution.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-[#5BB5A2] rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      <strong>Subject to Change:</strong> Lending criteria, interest rates, and loan terms 
                      are subject to change without notice. The information presented reflects criteria 
                      at the time of your pre-qualification check.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-[#5BB5A2] rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      <strong>No Guarantee of Funding:</strong> Pre-qualification does not guarantee that 
                      you will receive funding. Actual approval is at the sole discretion of the lending institution.
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* About Financify */}
            <div className="card-base" data-testid="about-section">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-[#5BB5A2]" />
                <h2 className="text-xl font-semibold text-[#0F3D3E]">About Financify</h2>
              </div>
              <div className="text-gray-600 space-y-4">
                <p>
                  Financify is a digital pre-qualification platform designed to help Bahamian residents 
                  understand their potential loan eligibility before applying to banks. We are not a 
                  lender and do not make loan decisions.
                </p>
                <p>
                  Our service provides an educational tool that:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#5BB5A2] rounded-full"></span>
                    Evaluates your information against general bank lending criteria
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#5BB5A2] rounded-full"></span>
                    Provides estimated loan amounts and payments
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#5BB5A2] rounded-full"></span>
                    Connects you with appropriate lending institutions
                  </li>
                </ul>
              </div>
            </div>

            {/* Privacy Policy */}
            <div className="card-base" data-testid="privacy-section">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-[#5BB5A2]" />
                <h2 className="text-xl font-semibold text-[#0F3D3E]">Privacy Policy</h2>
              </div>
              <div className="text-gray-600 space-y-4">
                <h3 className="font-semibold text-[#0F3D3E]">Information We Collect</h3>
                <p>
                  When you use our pre-qualification service, we collect the following information:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#5BB5A2] rounded-full"></span>
                    Employment information (type, status, tenure)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#5BB5A2] rounded-full"></span>
                    Income and financial information
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#5BB5A2] rounded-full"></span>
                    Contact information (name, email, phone)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#5BB5A2] rounded-full"></span>
                    Existing debt obligations
                  </li>
                </ul>

                <h3 className="font-semibold text-[#0F3D3E] mt-6">How We Use Your Information</h3>
                <p>
                  Your information is used solely for:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#5BB5A2] rounded-full"></span>
                    Processing your pre-qualification request
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#5BB5A2] rounded-full"></span>
                    Communicating with you about your inquiry
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#5BB5A2] rounded-full"></span>
                    Connecting you with lending institutions (only if you request it)
                  </li>
                </ul>

                <h3 className="font-semibold text-[#0F3D3E] mt-6">Data Security</h3>
                <p>
                  We implement appropriate security measures to protect your personal information. 
                  Your data is encrypted in transit and at rest, and access is restricted to 
                  authorized personnel only.
                </p>

                <h3 className="font-semibold text-[#0F3D3E] mt-6">No Credit Bureau Check</h3>
                <p>
                  Our pre-qualification process does <strong>not</strong> perform a credit bureau 
                  inquiry. Using our service will not affect your CRIF report.
                </p>
              </div>
            </div>

            {/* Contact */}
            <div className="card-base bg-[#5BB5A2]/5 border-[#5BB5A2]/20">
              <h2 className="text-xl font-semibold text-[#0F3D3E] mb-4">Questions?</h2>
              <p className="text-gray-600 mb-4">
                If you have any questions about this disclaimer or our privacy practices, 
                please contact us at support@financify.bs
              </p>
              <Link to="/qualify" data-testid="disclaimer-cta">
                <Button className="btn-primary">
                  Check My Eligibility
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Last Updated */}
          <p className="text-center text-sm text-gray-400 mt-8">
            Last updated: December 2024
          </p>
        </div>
      </div>
    </div>
  );
}
